import type { ComponentPropsWithRef, ImgHTMLAttributes, ReactNode, RefObject } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import classNames from 'classnames';
import { useDisableRightClick } from 'hooks/useDisableRightClick';
import { useHandleDevTools } from 'hooks/useHandleDevTools';
import { useIsScreenShoting } from 'hooks/useIsScreenShoting';
import { useIsWindowFocused } from 'hooks/useIsWindowFocues';

import { sendAnalyticEvent } from 'data/actions/analytics';

interface IAppImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  fallbackClassName?: string;
  errorFallback?: ReactNode;
  canCopy?: boolean;
  renderImage?: (
    props: ComponentPropsWithRef<'img'>,
    attrs: { canCopy: boolean; isScreenShoting: boolean }
  ) => ReactNode;
}

export const AppImage = forwardRef<HTMLImageElement, IAppImageProps>(
  (
    { src, alt, fallbackClassName, errorFallback = null, className, renderImage, canCopy = true, ...props },
    propsRef
  ) => {
    const localRef = useRef<HTMLImageElement>(null);
    const isScreenShoting = useIsScreenShoting();
    const devtools = useHandleDevTools();
    const isWindowFocused = useIsWindowFocused();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const dispatch = useDispatch();

    const ref = (propsRef as RefObject<HTMLImageElement> | null) || localRef;

    useDisableRightClick({ element: ref.current, disabled: canCopy });

    useEffect(() => {
      if (!src) {
        return;
      }

      const image = new Image();
      image.src = src;
      image.onload = () => {
        setIsLoading(false);
        dispatch(
          sendAnalyticEvent({
            event: 'image_load_status',
            data: {
              status: 'success',
              src: src
            }
          })
        );
      };
      image.onerror = () => {
        setIsError(true);
        dispatch(
          sendAnalyticEvent({
            event: 'image_load_status',
            data: {
              status: 'error',
              src: src
            }
          })
        );
      };
    }, [src, dispatch]);

    const imgProps: ComponentPropsWithRef<'img'> = {
      ...props,
      alt,
      src,
      ref,
      className: classNames(className, {
        'blur-[13.75px]': !canCopy && (isScreenShoting || !isWindowFocused)
      }),
      draggable: canCopy,
      style: {
        ...props.style,
        ...(canCopy === false && {
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none'
        })
      },
      onContextMenu: canCopy === false ? (e: React.MouseEvent) => e.preventDefault() : props.onContextMenu
    };

    if (isLoading) {
      return <div className={classNames('bg-black/[0.08]', fallbackClassName || className)} />;
    }

    if (isError && errorFallback) {
      return errorFallback;
    }

    if (devtools.isOpen && !canCopy) {
      return <div className={classNames('bg-black/[0.08]', className || fallbackClassName)} />;
    }

    if (renderImage) {
      return renderImage(imgProps, { canCopy, isScreenShoting });
    }

    return (
      <img
        alt={alt}
        src={src}
        {...imgProps}
      />
    );
  }
);
